/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
import { Observable, ServerError } from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import {
    Authenticator,
    AuthenticatorEdge,
    AuthenticatorQueryResult,
    TenantAccessMode,
    TenantAuthConfig,
} from "@/shared/services/graphql/generated/consumer-graph-types";

const FORBIDDEN = "forbidden";
const UNAUTHORIZED = "unauthorized";
const ANONYMOUS_STRATEGY = "AnonymousStrategy";
const TEST_STRATEGY = "TestStrategy";

interface AuthToken {
    token: string;
    expiresInSeconds: number;
}

let token: string;
let authConfig: TenantAuthConfig;
let authenticators: Authenticator[] = [];
let anonymousAuthenticator: Authenticator;

export const loginLink = onError(({ networkError, operation, forward }) => {
    const statusCode = (networkError as ServerError)?.statusCode;

    if (statusCode === 401 && !!token) {
        return new Observable((observer) => {
            getTokenAndStartTokenAutoRefresh()
                .then(() => {
                    const subscriber = {
                        next: observer.next.bind(observer),
                        error: observer.error.bind(observer),
                        complete: observer.complete.bind(observer),
                    };

                    forward(operation).subscribe(subscriber);
                })
                .catch((error: any) => {
                    if (error.message === UNAUTHORIZED) {
                        location.reload();
                        return;
                    }

                    observer.error(error);
                });
        });
    }

    if (statusCode === 403) {
        return new Observable((observer) => {
            observer.error("You don't have the rights to use the app. Please contact your administrator.");
        });
    }
});

export const authLink = setContext((_, { headers }) => {
    return {
        headers: {
            ...headers,
            authorization: getAuthHeader(),
        },
    };
});

export const getAuthHeader = () => {
    return token ? `Bearer ${token}` : "";
};

export const initAuthentication = (config: TenantAuthConfig) => {
    const authenticatorQueryResult = config?.authenticators as AuthenticatorQueryResult;
    if (!authenticatorQueryResult.authenticators?.length) throw new Error("No authenticator defined.");
    authConfig = config;
    setAuthenticators(authenticatorQueryResult.authenticators as AuthenticatorEdge[]);
};

export const tryGetToken = async () => {
    try {
        return await getTokenAndStartTokenAutoRefresh();
    } catch {
        return false;
    }
};

export const forceLogin = (authenticator: Authenticator, redirectUrl?: string) => {
    redirectToLoginPage(authenticator.url, redirectUrl);
};

const getTokenAndStartTokenAutoRefresh = async () => {
    token = "";
    const expiresIn = await getAndSetJWT();

    if (expiresIn) {
        tokenAutoRefresh(expiresIn);
        return true;
    }

    return false;
};

const setAnonymousAuthenticator = (auth: Authenticator) => {
    anonymousAuthenticator = auth;
};

const setAuthenticators = (authList: AuthenticatorEdge[]) => {
    const activeAuths = authList.filter((x) => x.node?.status !== "hidden");

    //check if tenant is for e2e ui testing
    const testStrategyAuth = activeAuths.find(
        (val: AuthenticatorEdge) => val?.node?.strategyName.toUpperCase() === TEST_STRATEGY.toUpperCase()
    );

    if (testStrategyAuth) {
        authenticators = [testStrategyAuth.node as Authenticator];
        return;
    }

    //check if tenant has anonymous strategy
    if (isMixedMode() || isAnonymousMode()) {
        const anonymousAuth = activeAuths.find(
            (val: AuthenticatorEdge) => val?.node?.strategyName === ANONYMOUS_STRATEGY
        );
        if (anonymousAuth) {
            setAnonymousAuthenticator(anonymousAuth.node as Authenticator);
        }
    }
    if (!isAnonymousMode()) {
        authenticators = activeAuths
            .filter((val: AuthenticatorEdge) => val?.node?.strategyName !== ANONYMOUS_STRATEGY)
            .map((x) => x.node) as Authenticator[];
    }
};

/**
 * Validates that a URL belongs to the current origin
 */
const isUrlSafe = (url: URL): boolean => {
    return url.origin === window.location.origin;
};

const getCallbackUrl = (redirectUrlString?: string): URL => {
    const defaultURL = new URL(window.location.href);

    if (!redirectUrlString) return defaultURL;

    try {
        const redirectURL = new URL(redirectUrlString, window.location.origin);

        if (!isUrlSafe(redirectURL)) {
            return defaultURL;
        }

        return redirectURL;
    } catch {
        console.warn(`Ignoring callback URL "${redirectUrlString}": not a valid URL.`);
        return defaultURL;
    }
};

/**
 * Resolves a callback URL to a router path (path + search + hash) of the current origin.
 * Returns undefined when the callback is missing or points to a foreign origin.
 */
export const resolveSafeRedirectPath = (redirectUrlString?: string): string | undefined => {
    if (!redirectUrlString) return undefined;

    const callbackURL = getCallbackUrl(redirectUrlString);
    if (callbackURL.href === window.location.href) return undefined;

    return `${callbackURL.pathname}${callbackURL.search}${callbackURL.hash}`;
};

/**
 * Redirects to the authenticator login page with validated callback URL
 */
const redirectToLoginPage = (authenticatorUrl: string, redirectUrl?: string): void => {
    const loginURL = new URL(authenticatorUrl);
    const callbackURL = getCallbackUrl(redirectUrl);

    loginURL.searchParams.set("loginDomain", window.location.origin);
    loginURL.searchParams.set("callbackURL", callbackURL.href);

    window.location.href = loginURL.href;
};

const getTokenFromCookie: () => Promise<AuthToken> = async () => {
    const tokenResponse = await fetch("/auth/tokens", {
        credentials: "include",
    });

    if (tokenResponse.status === 401) throw new Error(UNAUTHORIZED);
    if (tokenResponse.status === 403) throw new Error(FORBIDDEN);
    if (!tokenResponse.ok) throw new Error(tokenResponse.statusText);

    const tokenObject: AuthToken = await tokenResponse.json();
    return tokenObject;
};

const getAndSetJWT = async () => {
    const jwt = await getTokenFromCookie();
    token = jwt.token;
    return jwt.expiresInSeconds;
};

const tokenAutoRefresh = (expiresInSeconds: number, authenticate?: boolean) => {
    const intervalInMs = expiresInSeconds * 1000 * 0.9;
    setTimeout(async () => {
        try {
            const _expiresInSeconds = await getAndSetJWT();
            if (_expiresInSeconds) tokenAutoRefresh(_expiresInSeconds, authenticate);
        } catch (e: any) {
            if (e.message === UNAUTHORIZED) {
                location.reload();
            }
        }
    }, intervalInMs);
};

export const getAccessMode = () => {
    return authConfig.accessMode;
};

export const isMixedMode = () => {
    return authConfig.accessMode === TenantAccessMode.mixed;
};

export const isAnonymousMode = () => {
    return authConfig.accessMode === TenantAccessMode.anonymous;
};

export const isAuthenticatedMode = () => {
    return authConfig.accessMode === TenantAccessMode.authenticated;
};

export const getAuthenticators = () => {
    return authenticators;
};

export const getAnonymousAuthenticator = () => {
    if (!anonymousAuthenticator) throw new Error("No anonymous authenticator defined");
    return anonymousAuthenticator;
};

//Todo find better solutions, but right now router.beforeEach runs before login
export const waitForAuthentication = () => {
    return new Promise((resolve) => {
        if (token) {
            resolve(true);
            return;
        }

        const interval = setInterval(() => {
            if (token) {
                clearInterval(interval);
                resolve(true);
            }
        }, 100);
    });
};
